import { useState, useEffect, useCallback } from 'react';
import { Play } from 'lucide-react';

interface CarouselImage {
  url: string;
  attribution?: string;
  townName: string;
  type?: 'image' | 'video';
  videoId?: string;
}

interface HomeImageCarouselProps {
  images: CarouselImage[];
  autoPlayInterval?: number;
}

// Generate srcset for responsive images (Supabase Storage transform URLs)
function generateSrcSet(url: string): string {
  // Skip for non-Supabase URLs
  if (!url.includes('supabase.co/storage')) {
    return '';
  }
  
  const widths = [400, 600, 800, 1200];
  return widths
    .map(w => {
      // Add width transform parameter
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}width=${w} ${w}w`;
    })
    .join(', ');
}

export function HomeImageCarousel({ 
  images, 
  autoPlayInterval = 3000 
}: HomeImageCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [playingVideo, setPlayingVideo] = useState(false);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
    setPlayingVideo(false);
  }, [images.length]);

  useEffect(() => {
    if (images.length <= 1 || isPaused || playingVideo) return;

    const interval = setInterval(goToNext, autoPlayInterval);
    return () => clearInterval(interval);
  }, [images.length, isPaused, playingVideo, goToNext, autoPlayInterval]);

  // Reset playing state when index changes
  useEffect(() => {
    setPlayingVideo(false);
  }, [currentIndex]);

  if (images.length === 0) {
    return null;
  }

  const currentImage = images[currentIndex];
  const isVideo = currentImage.type === 'video' && currentImage.videoId;
  const isFirstImage = currentIndex === 0;
  const srcSet = generateSrcSet(currentImage.url);

  return (
    <div 
      className="relative w-full rounded-lg overflow-hidden shadow-md"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="aspect-video relative bg-black">
        {isVideo && playingVideo ? (
          <iframe
            src={`https://www.youtube.com/embed/${currentImage.videoId}?autoplay=1&rel=0`}
            title={`${currentImage.townName} - Video`}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : isVideo ? (
          <>
            <img
              src={currentImage.url}
              srcSet={srcSet || undefined}
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 70vw, 800px"
              alt={`${currentImage.townName} - Video thumbnail`}
              className="w-full h-full object-cover transition-opacity duration-500"
              loading="lazy"
              decoding="async"
              width={800}
              height={450}
            />
            {/* Play button overlay */}
            <button
              onClick={() => setPlayingVideo(true)}
              className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors group"
              aria-label="Play video"
            >
              <div className="bg-red-600 hover:bg-red-700 text-white rounded-full p-4 transform group-hover:scale-110 transition-transform shadow-lg">
                <Play className="h-8 w-8 fill-current" />
              </div>
            </button>
          </>
        ) : (
          <img
            src={currentImage.url}
            srcSet={srcSet || undefined}
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 70vw, 800px"
            alt={`${currentImage.townName} - Image ${currentIndex + 1}`}
            className="w-full h-full object-cover transition-opacity duration-500"
            loading={isFirstImage ? "eager" : "lazy"}
            decoding={isFirstImage ? "sync" : "async"}
            fetchPriority={isFirstImage ? "high" : undefined}
            width={800}
            height={450}
          />
        )}
        
        {/* Gradient overlay for text readability - hide when playing video */}
        {!playingVideo && (
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none" />
        )}
        
        {/* Attribution and town name - hide when playing video */}
        {!playingVideo && (
          <div className="absolute bottom-0 left-0 right-0 p-3 text-white">
            <p className="text-sm font-medium">{currentImage.townName}</p>
            {currentImage.attribution && (
              <p className="text-xs opacity-80 italic">
                {isVideo ? 'Video by' : 'Photo by'}: {currentImage.attribution}
              </p>
            )}
          </div>
        )}
        
        {/* Dot indicators - hide when playing video */}
        {images.length > 1 && !playingVideo && (
          <div className="absolute bottom-3 right-3 flex gap-1.5">
            {images.map((item, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentIndex 
                    ? 'bg-white scale-110' 
                    : 'bg-white/50 hover:bg-white/75'
                } ${item.type === 'video' ? 'ring-1 ring-red-500' : ''}`}
                aria-label={`Go to ${item.type === 'video' ? 'video' : 'image'} ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
